import { ApiClient } from "@/services/apiClient";
import type { MappedFilterValues } from "@/composables/filter";
import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import { CollectibleElastic_Schema } from "@/services/collectibles/elastic/collectiblesElasticSchema";
import type { CollectibleElastic } from "@/services/collectibles/elastic/collectiblesElasticTypes";
import type { CategoryId } from "@/services/collection/categories";
import { FilterDefinition_Schema } from "@/services/filter";
import { WholeNumber_Schema } from "@/utilities";
import { z } from "zod";

export const fetchCollectibleElasticById = async (id: CollectibleId): Promise<CollectibleElastic> => {
  const url = "api-public/collectible/collectible-elastic/" + id;
  return ApiClient.getPublic<CollectibleElastic>(url).then(({ data }) => CollectibleElastic_Schema.parse(data));
};

type SearchRequest = {
  categoryId: CategoryId;
  page: number;
  perPage: number;
  filters?: MappedFilterValues;
  identification?: string;
};

type SearchOptions = {
  signal?: AbortSignal;
};

const SearchResponse_Schema = z.object({
  totalItems: WholeNumber_Schema,
  items: z.array(CollectibleElastic_Schema),
  parameters: z.array(FilterDefinition_Schema),
  // sort: SortDefinition_Schema, @todo Not implemented in API yet
});

type SearchResponse = z.infer<typeof SearchResponse_Schema>;

export const searchCollectiblesElastic = async (request: SearchRequest, options?: SearchOptions): Promise<SearchResponse> => {
  const baseUrl = "api-public/collectible/collectible-elastic";

  const { categoryId, page, perPage, filters, identification } = request;

  // Build base params
  const params: Record<string, string | number | undefined> = {
    categoryId,
    page,
    perPage,
    identification: identification || undefined,
  };

  // Build filter query string with URLSearchParams (supports duplicate keys)
  const filterParams = new URLSearchParams();
  if (filters) {
    for (const [key, valueIds] of Object.entries(filters)) {
      const rangeMatch = key.match(/^(.+)_(min|max)$/);
      if (rangeMatch) {
        const [, definitionId, bound] = rangeMatch;
        filterParams.append(`parameters[][${definitionId}][${bound}]`, valueIds[0]);
      } else {
        for (const valueId of valueIds) {
          filterParams.append(`parameters[][${key}]`, valueId);
        }
      }
    }
  }

  // Append filter params to URL if any
  const filterQueryString = filterParams.toString();
  const url = filterQueryString ? `${baseUrl}?${filterQueryString}` : baseUrl;

  const response = await ApiClient.getPublic(url, params, undefined, undefined, options?.signal);
  return SearchResponse_Schema.parse(response.data);
};
